import type { Rect } from './layout';

/**
 * Presentation-only randomness.
 *
 * Deliberately NOT the run's gameplay rng. Particles, shake and animation
 * variation must never draw from the stream that decides rewards, or adding
 * three sparks to an effect would silently change what tomorrow's Daily seed
 * offers. Anything cosmetic goes through here; anything mechanical goes
 * through `run.rng`.
 */
let cosmeticState = (Date.now() ^ 0x9e3779b9) >>> 0;
export function cosmeticRandom(): number {
  cosmeticState = (cosmeticState + 0x6d2b79f5) >>> 0;
  let t = Math.imul(cosmeticState ^ (cosmeticState >>> 15), 1 | cosmeticState);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

interface FloatText {
  x: number;
  y: number;
  vy: number;
  life: number;
  maxLife: number;
  text: string;
  color: string;
  size: number;
}

interface Stamp {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  text: string;
  color: string;
  size: number;
}

/**
 * A projectile from an enemy panel to the HUD. Without it an attack is a
 * number that changes somewhere else on screen; with it, the hit visibly
 * travels, which is what makes the connection between cause and effect
 * readable instead of inferred.
 */
interface Tracer {
  x: number;
  y: number;
  tx: number;
  ty: number;
  t: number;
  dur: number;
  color: string;
  width: number;
}

export interface Fx {
  particles: Particle[];
  floats: FloatText[];
  stamps: Stamp[];
  tracers: Tracer[];
  /** Current shake magnitude in px. Decays; drives a random translate. */
  shake: number;
  /** White overlay alpha for row clears and cap hits. */
  flash: number;
  /** Red edge vignette — "you took damage", distinct from a white flash. */
  hurt: number;
  /**
   * Honour prefers-reduced-motion: no shake, no flash, no particles. The
   * floats stay, because they carry information rather than sensation.
   */
  reducedMotion: boolean;
}

const MAX_PARTICLES = 900;

export function createFx(reducedMotion = false): Fx {
  return {
    particles: [],
    floats: [],
    stamps: [],
    tracers: [],
    shake: 0,
    flash: 0,
    hurt: 0,
    reducedMotion,
  };
}

export function emitBurst(
  fx: Fx,
  x: number,
  y: number,
  color: string,
  count: number,
  speed = 130,
): void {
  if (fx.reducedMotion) return;
  for (let i = 0; i < count; i++) {
    if (fx.particles.length >= MAX_PARTICLES) return;
    const a = cosmeticRandom() * Math.PI * 2;
    const v = speed * (0.35 + cosmeticRandom() * 0.9);
    fx.particles.push({
      x,
      y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v - 40,
      life: 0,
      maxLife: 0.35 + cosmeticRandom() * 0.4,
      size: 2 + cosmeticRandom() * 3,
      color,
    });
  }
}

/** Sparks that travel from a point toward a target rect — Ember finding its mark. */
export function emitTrail(fx: Fx, fromX: number, fromY: number, to: Rect, color: string, count: number): void {
  if (fx.reducedMotion) return;
  const tx = to.x + to.w / 2;
  const ty = to.y + to.h / 2;
  for (let i = 0; i < count; i++) {
    if (fx.particles.length >= MAX_PARTICLES) return;
    fx.particles.push({
      x: fromX + (cosmeticRandom() - 0.5) * 8,
      y: fromY + (cosmeticRandom() - 0.5) * 8,
      vx: (tx - fromX) * (1.4 + cosmeticRandom()),
      vy: (ty - fromY) * (1.4 + cosmeticRandom()),
      life: 0,
      maxLife: 0.3 + cosmeticRandom() * 0.2,
      size: 2 + cosmeticRandom() * 2,
      color,
    });
  }
}

export function addFloat(fx: Fx, x: number, y: number, label: string, color: string, size = 15): void {
  if (fx.floats.length > 40) fx.floats.shift();
  fx.floats.push({ x, y, vy: -46, life: 0, maxLife: 0.9, text: label, color, size });
}

export function addStamp(fx: Fx, x: number, y: number, label: string, color: string, size = 30): void {
  if (fx.stamps.length > 12) fx.stamps.shift();
  fx.stamps.push({ x, y, life: 0, maxLife: 0.7, text: label, color, size });
}

/** Fire a projectile from one screen point to another. */
export function addTracer(
  fx: Fx,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  color: string,
  dur = 0.22,
  width = 3,
): void {
  if (fx.reducedMotion) return;
  if (fx.tracers.length > 12) fx.tracers.shift();
  fx.tracers.push({ x: fromX, y: fromY, tx: toX, ty: toY, t: 0, dur, color, width });
}

export function addShake(fx: Fx, amount: number): void {
  if (fx.reducedMotion) return;
  fx.shake = Math.min(24, fx.shake + amount);
}

export function addFlash(fx: Fx, alpha: number): void {
  if (fx.reducedMotion) return;
  fx.flash = Math.min(0.55, fx.flash + alpha);
}

/** Red edge vignette for taking damage. Reads as pain, not as a row clear. */
export function addHurt(fx: Fx, alpha: number): void {
  if (fx.reducedMotion) return;
  fx.hurt = Math.min(0.7, fx.hurt + alpha);
}

export function updateFx(fx: Fx, dt: number): void {
  for (let i = fx.particles.length - 1; i >= 0; i--) {
    const p = fx.particles[i];
    if (!p) continue;
    p.life += dt;
    if (p.life >= p.maxLife) {
      fx.particles.splice(i, 1);
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 240 * dt;
    p.vx *= 1 - 1.6 * dt;
  }

  for (let i = fx.floats.length - 1; i >= 0; i--) {
    const f = fx.floats[i];
    if (!f) continue;
    f.life += dt;
    if (f.life >= f.maxLife) {
      fx.floats.splice(i, 1);
      continue;
    }
    f.y += f.vy * dt;
    f.vy *= 1 - 1.2 * dt;
  }

  for (let i = fx.stamps.length - 1; i >= 0; i--) {
    const s = fx.stamps[i];
    if (!s) continue;
    s.life += dt;
    if (s.life >= s.maxLife) fx.stamps.splice(i, 1);
  }

  for (let i = fx.tracers.length - 1; i >= 0; i--) {
    const tr = fx.tracers[i];
    if (!tr) continue;
    tr.t += dt;
    if (tr.t >= tr.dur) fx.tracers.splice(i, 1);
  }

  fx.shake = Math.max(0, fx.shake - dt * 46);
  fx.flash = Math.max(0, fx.flash - dt * 2.4);
  fx.hurt = Math.max(0, fx.hurt - dt * 1.6);
}

/** Current shake offset. Apply as a translate before drawing the world. */
export function shakeOffset(fx: Fx): { x: number; y: number } {
  if (fx.shake <= 0.1) return { x: 0, y: 0 };
  return {
    x: (cosmeticRandom() - 0.5) * fx.shake * 2,
    y: (cosmeticRandom() - 0.5) * fx.shake * 2,
  };
}

export function drawFx(
  ctx: CanvasRenderingContext2D,
  fx: Fx,
  width: number,
  height: number,
): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  for (const p of fx.particles) {
    const k = 1 - p.life / p.maxLife;
    ctx.globalAlpha = Math.max(0, k) * 0.9;
    ctx.fillStyle = p.color;
    const s = p.size * (0.4 + k * 0.6);
    ctx.fillRect(p.x - s / 2, p.y - s / 2, s, s);
  }

  // Projectiles. A tapering trail with a bright head, so the eye follows the
  // hit from its source to where it lands.
  ctx.lineCap = 'round';
  for (const tr of fx.tracers) {
    const k = Math.min(1, tr.t / tr.dur);
    const hx = tr.x + (tr.tx - tr.x) * k;
    const hy = tr.y + (tr.ty - tr.y) * k;
    // Trail reaches back a fixed fraction of the path behind the head.
    const back = Math.max(0, k - 0.32);
    const bx = tr.x + (tr.tx - tr.x) * back;
    const by = tr.y + (tr.ty - tr.y) * back;

    ctx.globalAlpha = 0.75 * (1 - k * 0.35);
    ctx.strokeStyle = tr.color;
    ctx.lineWidth = tr.width;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(hx, hy);
    ctx.stroke();

    ctx.globalAlpha = 0.95;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(hx, hy, tr.width * 0.9, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  ctx.restore();
  ctx.save();

  for (const f of fx.floats) {
    const k = 1 - f.life / f.maxLife;
    ctx.globalAlpha = Math.max(0, Math.min(1, k * 1.6));
    ctx.font = `bold ${f.size}px ui-monospace, Menlo, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(13,11,20,0.85)';
    ctx.strokeText(f.text, f.x, f.y);
    ctx.fillStyle = f.color;
    ctx.fillText(f.text, f.x, f.y);
  }

  for (const s of fx.stamps) {
    const k = s.life / s.maxLife;
    // Punch in fast, hold, fade.
    const scale = k < 0.18 ? 0.5 + (k / 0.18) * 0.7 : 1.2 - Math.min(0.2, (k - 0.18) * 0.3);
    const alpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
    ctx.globalAlpha = Math.max(0, alpha);
    ctx.font = `bold ${Math.round(s.size * scale)}px ui-monospace, Menlo, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(13,11,20,0.9)';
    ctx.strokeText(s.text, s.x, s.y);
    ctx.fillStyle = s.color;
    ctx.fillText(s.text, s.x, s.y);
  }

  ctx.restore();

  if (fx.flash > 0.001) {
    ctx.save();
    ctx.globalAlpha = fx.flash;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  // Red vignette, strongest at the edges — the screen bleeding when you do.
  if (fx.hurt > 0.001) {
    ctx.save();
    const g = ctx.createRadialGradient(
      width / 2,
      height / 2,
      Math.min(width, height) * 0.25,
      width / 2,
      height / 2,
      Math.max(width, height) * 0.62,
    );
    g.addColorStop(0, 'rgba(255,40,40,0)');
    g.addColorStop(1, `rgba(255,40,40,${fx.hurt})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}
